import { css } from '@emotion/react';
import { RoomType } from '$/GraphQL/schema/schema';
import React from 'react';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { logger } from '$/utils/logger';
import isMobile from '$/utils/isMobile';
import { VimeoVideoQuality, VimeoVideoQualityObject } from '@vimeo/player';
import Button from '../../ui/Button/Button';
import DropdownComponent from '../../ui/Input/Basics/DropdownComponent';

export const QualityControlButton: React.FC<{
  className?: string;
}> = withWebinarPlayerContext(['player', 'currentRoom'])(({ className, context }) => {
  const [qualities, setQualities] = React.useState<VimeoVideoQualityObject[]>([]);
  const [quality, setQuality] = React.useState<VimeoVideoQuality>('auto');
  const { player, currentRoom } = context;

  React.useEffect(() => {
    if (!player) return;

    (async function requestQuality(): Promise<void> {
      const currentQuality = await player.getQuality();
      setQuality(currentQuality || 'auto');
    })();
  }, [player]);

  const getQualities = React.useCallback(async (): Promise<void> => {
    try {
      const qualities = await player?.getQualities();
      qualities && setQualities(qualities);
    } catch (e) {
      const error = e as Error;
      logger.error('Error setting player quality', error);
    }
  }, [player]);

  React.useEffect(() => {
    getQualities();
  }, [getQualities]);

  const qualityText = `${quality.capitalizeFirstLetter()}`;
  const isShown = currentRoom.type === RoomType.Presentation && qualities.length > 0;
  const qualityOptions = React.useMemo(() => {
    return qualities.map((quality) => {
      const label = quality.label;
      const id = quality.id as VimeoVideoQuality;
      return {
        label,
        value: id,
        onClick: (): void => {
          logger.debug('PLAY', 'SET QUALITY ', id);
          setQuality(id);
          player?.setQuality(id);
        },
      };
    });
  }, [player, qualities]);

  return (
    <div
      className={`control ${!isShown && 'hidden'}`}
      css={css`
        -webkit-tap-highlight-color: transparent;
        .dropdown-content {
          background: rgba(0, 0, 0, 0.8) !important;
          backdrop-filter: blur(4px);
          color: #fff !important;
          border: none !important;
          justify-items: center !important;
        }
      `}
    >
      <DropdownComponent>
        <DropdownComponent.Reference>
          <Button
            className={`${className} control appearance-none z-60 outline-none ${
              isMobile ? 'text-base' : 'text-xl'
            }`}
          >
            {qualityText}
          </Button>
        </DropdownComponent.Reference>
        <DropdownComponent.Options
          placement='top'
          className='bg-transparent shadow dropdown-content'
          modifiers={[{ name: 'flip', enabled: false }]}
        >
          {qualityOptions.map(({ label, value, onClick }) => (
            <DropdownComponent.Options.Item
              className={`
              ${isMobile ? 'px-1 text-base max-h-2e' : 'text-lg h-2_5e'}
                ${value === quality ? 'font-semibold justify-center' : 'opacity-70 justify-center'}
              `}
              key={value}
              onClick={onClick}
            >
              {label}
            </DropdownComponent.Options.Item>
          ))}
        </DropdownComponent.Options>
      </DropdownComponent>
    </div>
  );
});
